import React, { useState, useEffect } from 'react';
import * as mui from '@mui/material';
import PropTypes from 'prop-types';
import auditService from '../../../../services/AuditService';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import GppBadIcon from '@mui/icons-material/GppBad';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RecommendIcon from '@mui/icons-material/Recommend';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import SaveIcon from '@mui/icons-material/Save';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoIcon from '@mui/icons-material/Info';
import ClearIcon from '@mui/icons-material/Clear';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FilterListIcon from '@mui/icons-material/FilterList';
import MapIcon from '@mui/icons-material/Map';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

// TabPanel component for use with MUI Tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`control-mapping-tabpanel-${index}`}
      aria-labelledby={`control-mapping-tab-${index}`}
      {...other}
      style={{ height: 'calc(100% - 48px)', overflowY: 'auto' }} // Adjust height for tabs
    >
      {value === index && (
        <mui.Box sx={{ p: 2 }}>
          {children}
        </mui.Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `control-mapping-tab-${index}`,
    'aria-controls': `control-mapping-tabpanel-${index}`,
  };
}

const ControlMapper = ({ 
  app, 
  company, 
  appId, 
  companyId, 
  nextStep,
  prevStep,
  selectedRisk,
  selectRisk,
  refreshData
}) => {
  // State for risks and controls
  const [ratedRisks, setRatedRisks] = useState([]);
  const [filteredRatedRisks, setFilteredRatedRisks] = useState([]);
  const [availableControls, setAvailableControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [controlsLoading, setControlsLoading] = useState(true); // Separate loading for controls list
  const [mappedControlsLoading, setMappedControlsLoading] = useState(false); // Loading for mapped controls specifically
  const [searchTerm, setSearchTerm] = useState(''); // Search term for available controls
  const [riskSearchTerm, setRiskSearchTerm] = useState(''); // Search term for risks
  const [riskTypeFilter, setRiskTypeFilter] = useState('all');
  const [riskViewMode, setRiskViewMode] = useState('list'); // 'list' or 'group'
  const [filteredControls, setFilteredControls] = useState([]);
  const [recommendedControls, setRecommendedControls] = useState([]);
  const [showRecommended, setShowRecommended] = useState(false);
  
  // State for current selections
  const [currentRisk, setCurrentRisk] = useState(null);
  const [mappedControls, setMappedControls] = useState([]);
  
  // UI states
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [processingControlId, setProcessingControlId] = useState(null);
  const [createControlModalOpen, setCreateControlModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Mapped, 1: Add
  
  // Create Control states
  const [newControl, setNewControl] = useState({
    CONTROL_ID: '',
    CONTROL_TITLE: '',
    CONTROL_DESCRIPTION: '',
    CONTROL_TYPE: '',
    CONTROL_CATEGORY: '',
    CONTROL_DOMAIN: '',
  });
  const [controlErrors, setControlErrors] = useState({
    CONTROL_ID: '',
    CONTROL_TITLE: '',
    CONTROL_DESCRIPTION: ''
  });
  const [formValid, setFormValid] = useState(false);
  const [creatingControl, setCreatingControl] = useState(false);
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  
  // Constants for control dropdowns
  const controlTypeOptions = [
    { value: 'Preventive', label: 'Preventive' },
    { value: 'Detective', label: 'Detective' },
    { value: 'Monitoring', label: 'Monitoring' }
  ];

  const controlCategoryOptions = [
    { value: 'Business Process', label: 'Business Process' },
    { value: 'IT', label: 'IT' },
    { value: 'Operational', label: 'Operational' },
    { value: 'Entity Level', label: 'Entity Level' }
  ];

  const controlDomainOptions = [
    { value: 'Access Security', label: 'Access Security' },
    { value: 'Change Management', label: 'Change Management' },
    { value: 'IT Operations', label: 'IT Operations' },
    { value: 'Account Balances', label: 'Account Balances' }
  ];
  
  // Risk type options for filter
  const riskTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'IT', label: 'IT' },
    { value: 'Business Process', label: 'Business Process' },
    { value: 'Operational', label: 'Operational' },
    { value: 'Entity Level', label: 'Entity Level' }
  ];
  
  // Fetch initial data (risks and controls)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setControlsLoading(true);
      try {
        // Fetch risks first
        const mappingResponse = await auditService.fetchRiskMappingbyApp(appId, companyId);
        const mappedRiskData = mappingResponse?.risklist_data || [];
        const rated = mappedRiskData.filter(risk => risk.RATING && risk.RATING !== 'Not Set');
        
        // For each risk, check if it has controls mapped
        const risksWithMappingStatus = await Promise.all(rated.map(async (risk) => {
          try {
            const mappedControlsData = await auditService.fetchMappedControls(appId, risk.id, companyId);
            const hasMappedControls = mappedControlsData && 
                                     Array.isArray(mappedControlsData) && 
                                     mappedControlsData.length > 0 && 
                                     Array.isArray(mappedControlsData[0]?.CONTROL_DETAILS) && 
                                     mappedControlsData[0]?.CONTROL_DETAILS.length > 0;
            
            return {
              ...risk,
              HAS_MAPPED_CONTROLS: hasMappedControls,
              // Store the actual control details if available
              CONTROL_DETAILS: hasMappedControls ? mappedControlsData[0].CONTROL_DETAILS : []
            };
          } catch (error) {
            console.error(`Error fetching mapped controls for risk ${risk.id}:`, error);
            return {
              ...risk,
              HAS_MAPPED_CONTROLS: false,
              CONTROL_DETAILS: []
            };
          }
        }));
        
        setRatedRisks(risksWithMappingStatus);
        setFilteredRatedRisks(risksWithMappingStatus);

        // Determine initial risk selection
        let initialRisk = null;
        if (selectedRisk && selectedRisk.RATING && selectedRisk.RATING !== 'Not Set') {
          initialRisk = selectedRisk;
        } else if (risksWithMappingStatus.length > 0) {
          initialRisk = risksWithMappingStatus[0];
        }
        setCurrentRisk(initialRisk);

        // Fetch controls in parallel or after risks
        const controlsData = await auditService.fetchControls();
        setAvailableControls(controlsData || []); // Ensure it's an array
        setFilteredControls(controlsData || []);
        setControlsLoading(false);

        // If an initial risk is selected, fetch its mapped controls and generate recommendations
        if (initialRisk) {
          await fetchMappedControls(initialRisk.id);
          generateRecommendations(initialRisk, controlsData || []);
        }
        
      } catch (error) {
        console.error('Error loading initial data:', error);
        setControlsLoading(false);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, companyId]); // Only run once on mount essentially, selectedRisk handling is inside

  // Re-evaluate selectedRisk prop when it changes
  useEffect(() => {
    if (selectedRisk && ratedRisks.length > 0) {
        const foundRisk = ratedRisks.find(r => r.id === selectedRisk.id);
        if (foundRisk && foundRisk.RATING && foundRisk.RATING !== 'Not Set') {
            // Only update if the risk is different from current
            if (!currentRisk || currentRisk.id !== foundRisk.id) {
                handleSelectRisk(foundRisk);
            }
        } else if (currentRisk && currentRisk.id === selectedRisk.id) {
             // If the selectedRisk prop changes to something not rated/found, maybe clear selection?
             // Or keep the current one? For now, keep current.
        }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRisk, ratedRisks]);

  // Filter risks based on search term and type filter
  useEffect(() => {
    let filtered = [...ratedRisks];
    
    // Apply search term
    if (riskSearchTerm) {
      filtered = filtered.filter(
        risk => 
          risk.RISK_ID?.toLowerCase().includes(riskSearchTerm.toLowerCase()) ||
          risk.RISK_NAME?.toLowerCase().includes(riskSearchTerm.toLowerCase()) ||
          risk.RISK_DESCRIPTION?.toLowerCase().includes(riskSearchTerm.toLowerCase())
      );
    }
    
    // Apply risk type filter based on risk's mapped controls status
    if (riskTypeFilter === 'mitigated') {
      filtered = filtered.filter(risk => risk.HAS_MAPPED_CONTROLS === true);
    } else if (riskTypeFilter === 'unmitigated') {
      filtered = filtered.filter(risk => risk.HAS_MAPPED_CONTROLS !== true);
    }
    
    setFilteredRatedRisks(filtered);
  }, [riskSearchTerm, riskTypeFilter, ratedRisks]);

  // Group risks by type
  const groupRisksByType = (risks) => {
    const grouped = {};
    
    riskTypeOptions.forEach(option => {
      if (option.value !== 'all') {
        grouped[option.value] = risks.filter(risk => risk.RISK_TYPE === option.value);
      }
    });
    
    return grouped;
  };

  // Generate control recommendations based on risk
  const generateRecommendations = (risk, controlsList) => {
    if (!risk || !controlsList) {
      setRecommendedControls([]);
      return;
    }
    // Ensure controlsList is an array
    const safeControlsList = Array.isArray(controlsList) ? controlsList : [];
    if (safeControlsList.length === 0) {
      setRecommendedControls([]);
      return;
    }

    // Create a scoring system for controls based on risk attributes
    const scoredControls = safeControlsList.map(control => {
      let score = 0;
      
      // Match by risk type and control type
      if (risk.RISK_TYPE === 'IT' && control.CONTROL_TYPE === 'Technical') {
        score += 10;
      } else if (risk.RISK_TYPE === 'Business Process' && control.CONTROL_TYPE === 'Process') {
        score += 10;
      } else if (risk.RISK_TYPE === 'Operational' && control.CONTROL_TYPE === 'Operational') {
        score += 10;
      } else if (risk.RISK_TYPE === 'Entity Level' && control.CONTROL_TYPE === 'Entity Level') {
        score += 10;
      }
      
      // Risk rating based matching
      if (risk.RATING === 'High' && control.CONTROL_STRENGTH === 'Strong') {
        score += 5;
      } else if (risk.RATING === 'Medium' && 
                (control.CONTROL_STRENGTH === 'Moderate' || control.CONTROL_STRENGTH === 'Strong')) {
        score += 3;
      }
      
      // Keyword matching (simple implementation)
      const riskKeywords = extractKeywords(risk.RISK_NAME + ' ' + (risk.RISK_DESCRIPTION || ''));
      const controlKeywords = extractKeywords(control.CONTROL_TITLE + ' ' + (control.CONTROL_DESCRIPTION || ''));
      
      // Count matching keywords
      const matches = riskKeywords.filter(keyword => 
        controlKeywords.some(controlKeyword => 
          controlKeyword.includes(keyword) || keyword.includes(controlKeyword)
        )
      ).length;
      
      score += matches * 2;
      
      return {
        ...control,
        recommendationScore: score
      };
    });
    
    // Sort by score (descending) and take top matches
    const topRecommendations = scoredControls
      .sort((a, b) => b.recommendationScore - a.recommendationScore)
      .filter(control => control.recommendationScore > 0)
      .slice(0, 10);
    
    setRecommendedControls(topRecommendations);
  };
  
  // Extract simple keywords from text (simple implementation)
  const extractKeywords = (text) => {
    if (!text) return [];
    
    // Convert to lowercase, remove punctuation, split by spaces
    const words = text.toLowerCase()
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')
      .split(/\s+/);
    
    // Filter out common stop words and short words
    const stopWords = ['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'of', 'is', 'are'];
    return words.filter(word => word.length > 3 && !stopWords.includes(word));
  };

  // Fetch controls mapped to the current risk
  const fetchMappedControls = async (riskId) => {
    setMappedControlsLoading(true); // Start loading mapped controls
    try {
      const response = await auditService.fetchMappedControls(appId, riskId, companyId);
      // Ensure response structure and CONTROL_DETAILS are checked
      if (response && Array.isArray(response) && response.length > 0 && Array.isArray(response[0].CONTROL_DETAILS)) {
        setMappedControls(response[0].CONTROL_DETAILS);
      } else if (response && Array.isArray(response) && response.length > 0 && response[0].CONTROL_DETAILS === null) {
        setMappedControls([]); // Handle case where CONTROL_DETAILS is explicitly null
      } else {
        setMappedControls([]); // Default empty if structure is unexpected
      }
    } catch (error) {
      console.error('Error fetching mapped controls:', error);
      setMappedControls([]);
    } finally {
      setMappedControlsLoading(false); // Stop loading mapped controls
    }
  };

  // Filter controls based on search term or show recommendations
  useEffect(() => {
    // Ensure availableControls is an array before filtering
    const controlsToFilter = Array.isArray(availableControls) ? availableControls : [];
    let currentlyFiltered = [];

    if (showRecommended) {
      // Ensure recommendedControls is an array
      currentlyFiltered = Array.isArray(recommendedControls) ? recommendedControls : [];
    } else if (searchTerm) {
      currentlyFiltered = controlsToFilter.filter(
      control => 
        control.CONTROL_ID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        control.CONTROL_TITLE?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (control.CONTROL_DESCRIPTION && typeof control.CONTROL_DESCRIPTION === 'string' && 
           control.CONTROL_DESCRIPTION.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    } else {
        currentlyFiltered = controlsToFilter;
    }
    
    // --- New Filtering Step --- 
    // Exclude controls that are already mapped to the current risk
    if (currentRisk && Array.isArray(mappedControls)) {
        const mappedControlIds = new Set(mappedControls.map(mc => mc.id));
        currentlyFiltered = currentlyFiltered.filter(control => !mappedControlIds.has(control.id));
    }
    // --- End New Filtering Step ---

    setFilteredControls(currentlyFiltered);

  }, [searchTerm, availableControls, showRecommended, recommendedControls, currentRisk, mappedControls]); // Add currentRisk and mappedControls as dependencies

  // Handle selecting a risk to map controls to
  const handleSelectRisk = async (risk) => {
    if (!risk || risk.id === currentRisk?.id) return; // Avoid re-selecting the same risk

    setCurrentRisk(risk);
    setActiveTab(0); // Default to mapped controls tab on new risk selection
    setSearchTerm(''); // Clear control search on new risk selection
    setShowRecommended(false); // Turn off recommendations on new risk selection
    
    await fetchMappedControls(risk.id);
    // Ensure availableControls is loaded before generating recommendations
    const safeControls = Array.isArray(availableControls) ? availableControls : [];
    generateRecommendations(risk, safeControls);
    
    // If parent component has a selectRisk function, call it
    if (selectRisk) {
      selectRisk(risk);
    }
  };

  // Handle mapping a control to the current risk
  const handleMapControl = async (control) => {
    if (!currentRisk) return;
    
    setProcessingControlId(control.id);
    
    try {
      // Get the project ID from URL or localStorage or fetch from API if not available
      const urlParams = new URLSearchParams(window.location.search);
      let projectId = urlParams.get('project') || localStorage.getItem('currentProjectId');
      
      // If no project ID is found in URL or localStorage, we need to fetch available projects
      if (!projectId) {
        try {
          // Try to get the first available project for this company
          const projects = await auditService.fetchProjects();
          if (projects && projects.length > 0) {
            // Filter projects for this company
            const companyProjects = projects.filter(p => p.COMPANY_ID === companyId);
            if (companyProjects.length > 0) {
              projectId = companyProjects[0].id; // Use the first project's ID
              localStorage.setItem('currentProjectId', projectId); // Save for future use
            }
          }
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      }
      
      const mappingData = {
        CONTROL_ID: control.id,
        RISK_ID: currentRisk.id,
        APP_NAME: appId,
        COMPANY_ID: companyId,
        AUDIT_LIST: projectId // Use a valid project ID
      };
      
      await auditService.updateRiskMappingbyMapID(appId, currentRisk.id, companyId, mappingData);
      
      // Refresh mapped controls
      await fetchMappedControls(currentRisk.id);
      
      // Update the current risk's HAS_MAPPED_CONTROLS status
      setRatedRisks(prevRisks => {
        return prevRisks.map(risk => {
          if (risk.id === currentRisk.id) {
            return {
              ...risk,
              HAS_MAPPED_CONTROLS: true
            };
          }
          return risk;
        });
      });
      
      // Show success message
      setSuccessMessage(`Control "${control.CONTROL_ID}" mapped successfully`);
      setShowSuccessAlert(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
      
      // Refresh parent data to update the Overview dashboard
      if (refreshData) {
        refreshData();
      }
      
    } catch (error) {
      console.error('Error mapping control:', error);
    } finally {
      setProcessingControlId(null);
    }
  };

  // Handle unmapping a control from the current risk
  const handleUnmapControl = async (control) => {
    if (!currentRisk) return;
    
    setProcessingControlId(control.id);
    
    try {
      // Get the project ID from URL or localStorage or fetch from API if not available
      const urlParams = new URLSearchParams(window.location.search);
      let projectId = urlParams.get('project') || localStorage.getItem('currentProjectId');
      
      // If no project ID is found in URL or localStorage, we need to fetch available projects
      if (!projectId) {
        try {
          // Try to get the first available project for this company
          const projects = await auditService.fetchProjects();
          if (projects && projects.length > 0) {
            // Filter projects for this company
            const companyProjects = projects.filter(p => p.COMPANY_ID === companyId);
            if (companyProjects.length > 0) {
              projectId = companyProjects[0].id; // Use the first project's ID
              localStorage.setItem('currentProjectId', projectId); // Save for future use
            }
          }
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      }
      
      const unmapData = {
        ACTION: 'UNMAP',
        CONTROL_ID: control.id,
        RISK_ID: currentRisk.id,
        APP_NAME: appId,
        COMPANY_ID: companyId,
        AUDIT_LIST: projectId
      };
      
      await auditService.updateRiskMappingbyMapID(appId, currentRisk.id, companyId, unmapData);
      
      // Refresh mapped controls
      const mappedControlsResponse = await fetchMappedControls(currentRisk.id);
      
      // Update the current risk's HAS_MAPPED_CONTROLS status based on remaining controls
      const hasMappedControls = mappedControls.length > 1; // Check if there are other controls remaining
      
      setRatedRisks(prevRisks => {
        return prevRisks.map(risk => {
          if (risk.id === currentRisk.id) {
            return {
              ...risk,
              HAS_MAPPED_CONTROLS: hasMappedControls
            };
          }
          return risk;
        });
      });
      
      // Show success message
      setSuccessMessage(`Control "${control.CONTROL_ID}" unmapped`);
      setShowSuccessAlert(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
      
      // Refresh parent data to update the Overview dashboard
      if (refreshData) {
        refreshData();
      }
      
    } catch (error) {
      console.error('Error unmapping control:', error);
    } finally {
      setProcessingControlId(null);
    }
  };

  // Handle creating a workpaper for testing a control
  const handleCreateWorkpaper = async (control) => {
    if (!currentRisk || !control) return;
    
    setProcessingControlId(control.id);
    
    try {
      // Get the project ID from URL or localStorage or fetch from API if not available
      const urlParams = new URLSearchParams(window.location.search);
      let projectId = urlParams.get('project') || localStorage.getItem('currentProjectId');
      
      // If no project ID is found in URL or localStorage, we need to fetch available projects
      if (!projectId) {
        try {
          // Try to get the first available project for this company
          const projects = await auditService.fetchProjects();
          if (projects && projects.length > 0) {
            // Filter projects for this company
            const companyProjects = projects.filter(p => p.COMPANY_ID === companyId);
            if (companyProjects.length > 0) {
              projectId = companyProjects[0].id; // Use the first project's ID
              localStorage.setItem('currentProjectId', projectId); // Save for future use
            }
          }
        } catch (error) {
          console.error('Error fetching projects:', error);
        }
      }
      
      // Create workpaper data
      const workpaperData = {
        COMPANY_ID: companyId,
        APP_NAME: appId,
        CONTROL_ID: control.id,
        TITLE: `${control.CONTROL_ID} - ${control.CONTROL_TITLE}`,
        TYPE: 'Testing',
        STATUS: 'In Progress',
        AUDIT_LIST: projectId
      };
      
      const response = await auditService.createWorkpaper(workpaperData);
      
      if (response) {
        // Show success message
        setSuccessMessage('Workpaper created successfully');
        setShowSuccessAlert(true);
        
        // Hide success message after 3 seconds
        setTimeout(() => {
          setShowSuccessAlert(false);
        }, 3000);
        
        // Open workpaper in new tab
        window.open(`/audit/workpapers/project/${company?.id}/app/${appId}/control/${response.id}`, '_blank');
      }
      
    } catch (error) {
      console.error('Error creating workpaper:', error);
    } finally {
      setProcessingControlId(null);
    }
  };

  // Check if a control is already mapped
  const isControlMapped = (controlId) => {
    return mappedControls.some(control => control.id === controlId);
  };

  // Get risk rating badge color
  const getRatingColor = (rating) => {
    switch(rating) {
      case 'High': return '#d32f2f';
      case 'Medium': return '#ff9800';
      case 'Low': return '#2e7d32';
      default: return '#757575';
    }
  };

  // Get risk icon based on type
  const getRiskTypeIcon = (type) => {
    switch(type) {
      case 'IT': return <SecurityIcon fontSize="small" />;
      case 'Business Process': return <ErrorOutlineIcon fontSize="small" />;
      case 'Operational': return <WarningIcon fontSize="small" />;
      case 'Entity Level': return <GppBadIcon fontSize="small" />;
      default: return <ErrorOutlineIcon fontSize="small" />;
    }
  };

  // Handle control form changes
  const handleControlChange = (e) => {
    const { name, value } = e.target;
    
    setNewControl(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate field
    validateField(name, value);
  };
  
  // Handle control type/category/domain selection
  const handleControlFieldChange = (field, value) => {
    setNewControl(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Validate a single field
  const validateField = (name, value) => {
    let errorMessage = '';
    
    switch (name) {
      case 'CONTROL_ID':
        if (!value.trim()) {
          errorMessage = 'Control ID is required';
        } else if (availableControls.some(c => c.CONTROL_ID === value)) {
          errorMessage = 'Control ID already exists';
        }
        break;
      case 'CONTROL_TITLE':
        if (!value.trim()) {
          errorMessage = 'Control Name is required';
        }
        break;
      case 'CONTROL_DESCRIPTION':
        if (!value.trim()) {
          errorMessage = 'Control Description is required';
        }
        break;
      default:
        break;
    }
    
    setControlErrors(prev => ({
      ...prev,
      [name]: errorMessage
    }));
  };
  
  // Validate entire form
  useEffect(() => {
    const validId = newControl.CONTROL_ID && !controlErrors.CONTROL_ID;
    const validTitle = newControl.CONTROL_TITLE && !controlErrors.CONTROL_TITLE;
    const validDescription = newControl.CONTROL_DESCRIPTION && !controlErrors.CONTROL_DESCRIPTION;
    
    setFormValid(validId && validTitle && validDescription);
  }, [newControl, controlErrors]);
  
  // Generate control description using AI
  const generateControlDescription = async () => {
    if (!newControl.CONTROL_TITLE) {
      setSuccessMessage('Please enter a Control Name before generating a description');
      setShowSuccessAlert(true);
      return;
    }
    
    setDescriptionLoading(true);
    try {
      const response = await auditService.generateControlDescription({
        control_title: newControl.CONTROL_TITLE,
        control_type: newControl.CONTROL_TYPE || '',
        control_category: newControl.CONTROL_CATEGORY || '',
        control_domain: newControl.CONTROL_DOMAIN || ''
      });
      
      setNewControl(prev => ({
        ...prev,
        CONTROL_DESCRIPTION: response.description
      }));
      
      // Clear any error
      setControlErrors(prev => ({
        ...prev,
        CONTROL_DESCRIPTION: ''
      }));
      
    } catch (error) {
      console.error('Error generating description:', error);
      setSuccessMessage('Failed to generate control description');
      setShowSuccessAlert(true);
    } finally {
      setDescriptionLoading(false);
    }
  };
  
  // Create a new control
  const createControl = async () => {
    if (!formValid) {
      setSuccessMessage('Please fill all required fields correctly');
      setShowSuccessAlert(true);
      return;
    }
    
    setCreatingControl(true);
    try {
      const createdControl = await auditService.createControls(newControl);
      
      if (createdControl) {
        // Add to available controls
        setAvailableControls(prev => [createdControl, ...prev]);
        setFilteredControls(prev => [createdControl, ...prev]);
        
        // Close modal and show success message
        setCreateControlModalOpen(false);
        setSuccessMessage('Control created successfully');
        setShowSuccessAlert(true);
        
        // Reset form
        setNewControl({
          CONTROL_ID: '',
          CONTROL_TITLE: '',
          CONTROL_DESCRIPTION: '',
          CONTROL_TYPE: '',
          CONTROL_CATEGORY: '',
          CONTROL_DOMAIN: '',
        });
        
        // Refresh parent data to update the Overview dashboard
        if (refreshData) {
          refreshData();
        }
      }
    } catch (error) {
      console.error('Error creating control:', error);
      setSuccessMessage('Error creating control: ' + (error.message || 'Unknown error'));
      setShowSuccessAlert(true);
    } finally {
      setCreatingControl(false);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset search/filter when switching tabs
    if (newValue === 1) { // Switched to "Add Controls" tab
        // Recommendations should already be generated by handleSelectRisk
        // or when availableControls loaded. Ensure filtered list is reset if needed.
        const controlsToFilter = Array.isArray(availableControls) ? availableControls : [];
        setFilteredControls(controlsToFilter); // Show all available initially
        setSearchTerm('');
        setShowRecommended(false);
    } else { // Switched away from "Add Controls" tab (to Mapped)
        setSearchTerm('');
        setShowRecommended(false);
    }
  };

  return (
    <mui.Box>
      {/* Success notification */}
      <mui.Snackbar
        open={showSuccessAlert}
        autoHideDuration={3000}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <mui.Alert
          severity="success"
          variant="filled"
          icon={<CheckCircleIcon fontSize="inherit" />}
        >
          {successMessage}
        </mui.Alert>
      </mui.Snackbar>
      
      {/* Create Control Modal */}
      <mui.Dialog 
        open={createControlModalOpen} 
        onClose={() => setCreateControlModalOpen(false)}
        maxWidth="md"
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2 }
        }}
      >
        <mui.DialogTitle sx={{ 
          borderBottom: '1px solid', 
          borderColor: 'divider',
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <AddCircleOutlineIcon color="primary" />
          <mui.Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Create New Control
          </mui.Typography>
        </mui.DialogTitle>
        
        <mui.DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
          <mui.Grid container spacing={2}>
            <mui.Grid item xs={12} md={6}>
              <mui.Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Control ID <mui.Typography component="span" color="error">*</mui.Typography>
              </mui.Typography>
              <mui.TextField
                name="CONTROL_ID"
                value={newControl.CONTROL_ID}
                onChange={handleControlChange}
                placeholder="Enter unique control ID"
                fullWidth
                size="small"
                variant="outlined"
                error={!!controlErrors.CONTROL_ID}
                helperText={controlErrors.CONTROL_ID || ''}
                sx={{ mb: 2 }}
              />
              
              <mui.Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Control Name <mui.Typography component="span" color="error">*</mui.Typography>
              </mui.Typography>
              <mui.TextField
                name="CONTROL_TITLE"
                value={newControl.CONTROL_TITLE}
                onChange={handleControlChange}
                placeholder="Enter descriptive name"
                fullWidth
                size="small"
                variant="outlined"
                error={!!controlErrors.CONTROL_TITLE}
                helperText={controlErrors.CONTROL_TITLE || ''}
                sx={{ mb: 2 }}
              />
              
              <mui.Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Control Type
              </mui.Typography>
              <mui.FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <mui.Select
                  value={newControl.CONTROL_TYPE}
                  onChange={(e) => handleControlFieldChange('CONTROL_TYPE', e.target.value)}
                  displayEmpty
                >
                  <mui.MenuItem value="" disabled>Select control type</mui.MenuItem>
                  {controlTypeOptions.map(option => (
                    <mui.MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            
            <mui.Grid item xs={12} md={6}>
              <mui.Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Control Category
              </mui.Typography>
              <mui.FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <mui.Select
                  value={newControl.CONTROL_CATEGORY}
                  onChange={(e) => handleControlFieldChange('CONTROL_CATEGORY', e.target.value)}
                  displayEmpty
                >
                  <mui.MenuItem value="" disabled>Select control category</mui.MenuItem>
                  {controlCategoryOptions.map(option => (
                    <mui.MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
              
              <mui.Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 500 }}>
                Control Domain
              </mui.Typography>
              <mui.FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <mui.Select
                  value={newControl.CONTROL_DOMAIN}
                  onChange={(e) => handleControlFieldChange('CONTROL_DOMAIN', e.target.value)}
                  displayEmpty
                >
                  <mui.MenuItem value="" disabled>Select control domain</mui.MenuItem>
                  {controlDomainOptions.map(option => (
                    <mui.MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </mui.MenuItem>
                  ))}
                </mui.Select>
              </mui.FormControl>
            </mui.Grid>
            
            <mui.Grid item xs={12}>
              <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <mui.Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                  Control Description <mui.Typography component="span" color="error">*</mui.Typography>
                </mui.Typography>
                <mui.Button 
                  variant="text"
                  size="small"
                  color="primary"
                  onClick={generateControlDescription}
                  disabled={descriptionLoading || !newControl.CONTROL_TITLE}
                  startIcon={descriptionLoading ? <mui.CircularProgress size={16} /> : <AutoAwesomeIcon fontSize="small" />}
                  sx={{ textTransform: 'none' }}
                >
                  Generate with AI
                </mui.Button>
              </mui.Box>
              <mui.TextField
                name="CONTROL_DESCRIPTION"
                value={newControl.CONTROL_DESCRIPTION}
                onChange={handleControlChange}
                placeholder="Enter detailed control description"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                error={!!controlErrors.CONTROL_DESCRIPTION}
                helperText={controlErrors.CONTROL_DESCRIPTION || ''}
              />
            </mui.Grid>
          </mui.Grid>
          
          <mui.Box sx={{ mt: 3, mb: 1, px: 1 }}>
            <mui.Alert severity="info" icon={<InfoIcon fontSize="inherit" />} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <mui.Typography variant="body2">
                Creating a control here will add it to the control library and make it available for all risks.
              </mui.Typography>
            </mui.Alert>
          </mui.Box>
        </mui.DialogContent>
        
        <mui.DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <mui.Button 
            variant="outlined" 
            onClick={() => setCreateControlModalOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </mui.Button>
          <mui.Button
            variant="contained"
            color="primary"
            onClick={createControl}
            disabled={!formValid || creatingControl}
            startIcon={creatingControl ? <mui.CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {creatingControl ? 'Creating...' : 'Create Control'}
          </mui.Button>
        </mui.DialogActions>
      </mui.Dialog>
      
      <mui.Grid container spacing={3}>
        {/* Left panel - Risks list */}
        <mui.Grid item xs={12} md={4}>
          <mui.Paper 
            elevation={1} 
            sx={{ 
              height: 'calc(100vh - 200px)', // Adjust height as needed
              p: 2, 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <mui.Typography variant="h6">
                Rated Risks
                <mui.Chip 
                  label={filteredRatedRisks.length} 
                  size="small" 
                  sx={{ ml: 1 }} 
                  color="primary"
                />
              </mui.Typography>
              
              <mui.ToggleButtonGroup
                value={riskViewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode) setRiskViewMode(newMode);
                }}
                aria-label="risk view mode"
                size="small"
              >
                <mui.Tooltip title="List View">
                  <mui.ToggleButton value="list" aria-label="list view">
                    <ViewListIcon fontSize="small" />
                  </mui.ToggleButton>
                </mui.Tooltip>
                <mui.Tooltip title="Group by Type">
                  <mui.ToggleButton value="group" aria-label="group view">
                    <ViewModuleIcon fontSize="small" />
                  </mui.ToggleButton>
                </mui.Tooltip>
              </mui.ToggleButtonGroup>
            </mui.Box>
            
            <mui.Box sx={{ mb: 2 }}>
              <mui.TextField
                placeholder="Search risks..."
                variant="outlined"
                size="small"
                fullWidth
                value={riskSearchTerm}
                onChange={(e) => setRiskSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <mui.InputAdornment position="start">
                      <SearchIcon color="action" />
                    </mui.InputAdornment>
                  ),
                  endAdornment: riskSearchTerm && (
                    <mui.InputAdornment position="end">
                      <mui.IconButton size="small" onClick={() => setRiskSearchTerm('')}>
                        <mui.Icon fontSize="small">clear</mui.Icon>
                      </mui.IconButton>
                    </mui.InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
              
              <mui.Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <mui.Chip
                  label={
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <mui.Typography variant="body2">All Risks</mui.Typography>
                      <mui.Chip 
                        size="small" 
                        label={ratedRisks.length} 
                        sx={{ 
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    </mui.Box>
                  }
                  onClick={() => setRiskTypeFilter('all')}
                  color={riskTypeFilter === 'all' ? 'primary' : 'default'}
                  variant={riskTypeFilter === 'all' ? 'filled' : 'outlined'}
                  sx={{ 
                    '& .MuiChip-label': { p: 1 },
                    backgroundColor: riskTypeFilter === 'all' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: riskTypeFilter === 'all' ? 'primary.dark' : 'action.hover'
                    }
                  }}
                />
                <mui.Chip
                  label={
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <mui.Typography variant="body2">Mitigated</mui.Typography>
                      <mui.Chip 
                        size="small" 
                        label={ratedRisks.filter(r => r.HAS_MAPPED_CONTROLS === true).length}
                        sx={{ 
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    </mui.Box>
                  }
                  onClick={() => setRiskTypeFilter('mitigated')}
                  color={riskTypeFilter === 'mitigated' ? 'primary' : 'default'}
                  variant={riskTypeFilter === 'mitigated' ? 'filled' : 'outlined'}
                  sx={{ 
                    '& .MuiChip-label': { p: 1 },
                    backgroundColor: riskTypeFilter === 'mitigated' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: riskTypeFilter === 'mitigated' ? 'primary.dark' : 'action.hover'
                    }
                  }}
                />
                <mui.Chip
                  label={
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <mui.Typography variant="body2">Needs Action</mui.Typography>
                      <mui.Chip 
                        size="small" 
                        label={ratedRisks.filter(r => r.HAS_MAPPED_CONTROLS !== true).length}
                        sx={{ 
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    </mui.Box>
                  }
                  onClick={() => setRiskTypeFilter('unmitigated')}
                  color={riskTypeFilter === 'unmitigated' ? 'primary' : 'default'}
                  variant={riskTypeFilter === 'unmitigated' ? 'filled' : 'outlined'}
                  sx={{ 
                    '& .MuiChip-label': { p: 1 },
                    backgroundColor: riskTypeFilter === 'unmitigated' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: riskTypeFilter === 'unmitigated' ? 'primary.dark' : 'action.hover'
                    }
                  }}
                />
              </mui.Box>
            </mui.Box>
            
            {loading ? (
              <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <mui.CircularProgress />
              </mui.Box>
            ) : filteredRatedRisks.length === 0 ? (
              <mui.Box 
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <mui.Typography variant="subtitle1" color="textSecondary" gutterBottom>
                  {riskSearchTerm || riskTypeFilter !== 'all' ? 
                    'No risks match your criteria' : 
                    'No rated risks'
                  }
                </mui.Typography>
                <mui.Typography variant="body2" color="textSecondary">
                  {riskSearchTerm || riskTypeFilter !== 'all' ? 
                    'Try adjusting your search or filters.' : 
                    'You need to rate your identified risks before mapping controls.'
                  }
                </mui.Typography>
                {(riskSearchTerm || riskTypeFilter !== 'all') && (
                  <mui.Button 
                    variant="outlined" 
                    color="primary" 
                    sx={{ mt: 2, alignSelf: 'center' }}
                    onClick={() => {
                      setRiskSearchTerm('');
                      setRiskTypeFilter('all');
                    }}
                  >
                    Clear Filters
                  </mui.Button>
                )}
                {(!riskSearchTerm && riskTypeFilter === 'all' && ratedRisks.length === 0) && (
                  <mui.Button 
                    variant="outlined" 
                    color="primary" 
                    sx={{ mt: 2, alignSelf: 'center' }}
                    onClick={prevStep}
                    startIcon={<ArrowBackIcon />}
                  >
                    Rate Risks
                  </mui.Button>
                )}
              </mui.Box>
            ) : riskViewMode === 'list' ? (
              <mui.List 
                sx={{ 
                  overflow: 'auto', 
                  flexGrow: 1, 
                  '& .MuiListItemButton-root.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.12)'
                  }
                }}
              >
                {filteredRatedRisks.map(risk => (
                  <mui.ListItem 
                    key={risk.id}
                    disablePadding
                    sx={{ mb: 1 }}
                  >
                    <mui.ListItemButton 
                      onClick={() => handleSelectRisk(risk)}
                      selected={currentRisk?.id === risk.id}
                      sx={{ borderRadius: 1 }}
                    >
                      <mui.ListItemAvatar>
                        <mui.Badge
                          badgeContent={
                            risk.CONTROL_DETAILS && risk.CONTROL_DETAILS.length 
                              ? risk.CONTROL_DETAILS.length 
                              : 0
                          }
                          color="primary"
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                        >
                          <mui.Avatar 
                            sx={{ 
                              bgcolor: `${getRatingColor(risk.RATING)}20`,
                              color: getRatingColor(risk.RATING),
                              width: 36, 
                              height: 36
                            }}
                          >
                            {getRiskTypeIcon(risk.RISK_TYPE)}
                          </mui.Avatar>
                        </mui.Badge>
                      </mui.ListItemAvatar>
                      <mui.ListItemText
                        primary={
                          <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <mui.Typography variant="subtitle2" noWrap>
                              {risk.RISK_ID}
                            </mui.Typography>
                            {risk.RATING && (
                              <mui.Chip 
                                size="small"
                                label={risk.RATING}
                                sx={{ 
                                  backgroundColor: `${getRatingColor(risk.RATING)}15`,
                                  color: getRatingColor(risk.RATING),
                                  fontWeight: 'bold',
                                  height: 24
                                }}
                              />
                            )}
                          </mui.Box>
                        }
                        secondary={
                          <mui.Typography 
                            variant="body2" 
                            color="textSecondary" 
                            sx={{
                              display: '-webkit-box',
                              overflow: 'hidden',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 1
                            }}
                          >
                            {risk.RISK_NAME}
                          </mui.Typography>
                        }
                      />
                    </mui.ListItemButton>
                  </mui.ListItem>
                ))}
              </mui.List>
            ) : (
              <mui.Box sx={{ overflow: 'auto', flexGrow: 1 }}>
                {Object.entries(groupRisksByType(filteredRatedRisks)).map(([type, risks]) => 
                  risks.length > 0 && (
                    <mui.Box key={type} sx={{ mb: 3 }}>
                      <mui.Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 1,
                          pb: 0.5,
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <mui.Avatar 
                          sx={{ 
                            bgcolor: 'primary.main', 
                            width: 24, 
                            height: 24,
                            mr: 1
                          }}
                        >
                          {getRiskTypeIcon(type)}
                        </mui.Avatar>
                        <mui.Typography variant="subtitle2" color="primary">
                          {type}
                        </mui.Typography>
                        <mui.Chip 
                          label={risks.length} 
                          size="small" 
                          sx={{ ml: 1 }} 
                          color="primary"
                          variant="outlined"
                        />
                      </mui.Box>
                      
                      <mui.List sx={{ pl: 1 }}>
                        {risks.map(risk => (
                          <mui.ListItem 
                            key={risk.id}
                            disablePadding
                            sx={{ mb: 0.5 }}
                          >
                            <mui.ListItemButton 
                              onClick={() => handleSelectRisk(risk)}
                              selected={currentRisk?.id === risk.id}
                              sx={{ 
                                borderRadius: 1,
                                py: 0.5
                              }}
                            >
                              <mui.ListItemAvatar sx={{ minWidth: 40 }}>
                                <mui.Badge
                                  badgeContent={
                                    risk.CONTROL_DETAILS && risk.CONTROL_DETAILS.length 
                                      ? risk.CONTROL_DETAILS.length 
                                      : 0
                                  }
                                  color="primary"
                                  anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'right',
                                  }}
                                  sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: '16px', minWidth: '16px' } }}
                                >
                                  <mui.Avatar 
                                    sx={{ 
                                      bgcolor: `${getRatingColor(risk.RATING)}20`,
                                      color: getRatingColor(risk.RATING),
                                      width: 28, 
                                      height: 28
                                    }}
                                  >
                                    <mui.Typography variant="caption" fontWeight="bold">
                                      {risk.RATING?.charAt(0) || '?'}
                                    </mui.Typography>
                                  </mui.Avatar>
                                </mui.Badge>
                              </mui.ListItemAvatar>
                              <mui.ListItemText
                                primary={
                                  <mui.Typography variant="body2" fontWeight="medium">
                                    {risk.RISK_ID}
                                  </mui.Typography>
                                }
                                secondary={
                                  <mui.Typography 
                                    variant="caption" 
                                    color="textSecondary" 
                                    sx={{
                                      display: '-webkit-box',
                                      overflow: 'hidden',
                                      WebkitBoxOrient: 'vertical',
                                      WebkitLineClamp: 1
                                    }}
                                  >
                                    {risk.RISK_NAME}
                                  </mui.Typography>
                                }
                              />
                            </mui.ListItemButton>
                          </mui.ListItem>
                        ))}
                      </mui.List>
                    </mui.Box>
                  )
                )}
              </mui.Box>
            )}
          </mui.Paper>
        </mui.Grid>
        
        {/* Right panel - Selected Risk Details & Control Management */}
        <mui.Grid item xs={12} md={8}>
          <mui.Paper 
            elevation={1} 
            sx={{ 
              height: 'calc(100vh - 200px)', // Adjust height to match left panel
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden' // Prevent content overflow before tab panel scrolling
            }}
          >
            {!currentRisk ? (
              <mui.Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  p: 3,
                  textAlign: 'center'
                }}
              >
                <SecurityIcon color="action" sx={{ fontSize: 64, mb: 2, opacity: 0.6 }} />
                <mui.Typography variant="h6" color="textSecondary" gutterBottom>
                  Select a Risk
                </mui.Typography>
                <mui.Typography 
                  variant="body1" 
                  color="textSecondary" 
                  sx={{ maxWidth: 450, mb: 3 }}
                >
                  Choose a rated risk from the list on the left to view its details and manage control mappings.
                </mui.Typography>
                <mui.Button
                  variant="outlined"
                  color="primary"
                  onClick={prevStep}
                  startIcon={<ArrowBackIcon />}
                >
                  Back to Risk Rating
                </mui.Button>
              </mui.Box>
            ) : (
              <>
                {/* Selected Risk Header - Minimalist Design */}
                <mui.Paper 
                  elevation={0}
                  sx={{ 
                    mb: 3,
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {/* Risk Header */}
                  <mui.Box 
                    sx={{ 
                      p: 2.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper'
                    }}
                  >
                    <mui.Grid container spacing={2} alignItems="center">
                      {/* Risk Icon */}
                      <mui.Grid item>
                        <mui.Avatar 
                          sx={{ 
                            bgcolor: `${getRatingColor(currentRisk.RATING)}10`,
                            color: getRatingColor(currentRisk.RATING),
                            width: 48, 
                            height: 48
                          }}
                        >
                          {getRiskTypeIcon(currentRisk.RISK_TYPE)}
                        </mui.Avatar>
                      </mui.Grid>
                      
                      {/* Risk ID and Name */}
                      <mui.Grid item xs>
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <mui.Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            {currentRisk.RISK_ID}
                          </mui.Typography>
                          <mui.Chip 
                            size="small" 
                            label={currentRisk.RISK_TYPE} 
                            variant="outlined"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              ml: 1
                            }}
                          />
                        </mui.Box>
                        <mui.Typography 
                          variant="subtitle1" 
                          sx={{ fontWeight: 500, lineHeight: 1.3 }}
                        >
                          {currentRisk.RISK_NAME}
                        </mui.Typography>
                      </mui.Grid>
                      
                      {/* Rating Badge */}
                      <mui.Grid item>
                        <mui.Chip 
                          label={currentRisk.RATING} 
                          size="small"
                          sx={{ 
                            bgcolor: `${getRatingColor(currentRisk.RATING)}10`,
                            color: getRatingColor(currentRisk.RATING),
                            fontWeight: 'medium',
                            minWidth: 70,
                            border: '1px solid',
                            borderColor: `${getRatingColor(currentRisk.RATING)}30`
                          }}
                        />
                      </mui.Grid>
                    </mui.Grid>
                  </mui.Box>
                  
                  {/* Risk Description - Only show if it exists */}
                  {currentRisk.RISK_DESCRIPTION && (
                    <mui.Box sx={{ px: 2.5, py: 2, bgcolor: 'grey.50' }}>
                      <mui.Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        Description
                      </mui.Typography>
                      <mui.Typography variant="body2" color="text.primary">
                        {currentRisk.RISK_DESCRIPTION}
                      </mui.Typography>
                    </mui.Box>
                  )}
                </mui.Paper>
                
                {/* Tabs for Mapped/Add Controls */}
                <mui.Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <mui.Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    aria-label="Control mapping tabs"
                    variant="fullWidth" // Make tabs fill the width
                  >
                    <mui.Tab 
                      icon={<PlaylistAddCheckIcon />} 
                      iconPosition="start" 
                      label={`Mapped (${mappedControls.length})`} 
                      {...a11yProps(0)} 
                      sx={{ textTransform: 'none' }} 
                    />
                    <mui.Tab 
                      icon={<PlaylistAddIcon />} 
                      iconPosition="start" 
                      label="Add Controls" 
                      {...a11yProps(1)} 
                      sx={{ textTransform: 'none' }} 
                    />
                  </mui.Tabs>
                </mui.Box>
                
                {/* Tab Panel for Mapped Controls */}
                <TabPanel value={activeTab} index={0}>
                  {mappedControlsLoading ? (
                  <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <mui.CircularProgress />
                  </mui.Box>
                ) : mappedControls.length === 0 ? (
                  <mui.Box 
                    sx={{ 
                      p: 3, 
                      textAlign: 'center',
                      backgroundColor: '#f9f9f9',
                      borderRadius: 2,
                        mt: 2 // Add margin top
                    }}
                  >
                    <mui.Typography variant="subtitle1" color="textSecondary" gutterBottom>
                      No controls mapped yet
                    </mui.Typography>
                    <mui.Typography variant="body2" color="textSecondary">
                        Use the "Add Controls" tab to find and map relevant controls from the library.
                    </mui.Typography>
                       <mui.Button 
                        variant="outlined" 
                        color="primary" 
                        sx={{ mt: 2 }}
                        onClick={() => setActiveTab(1)} // Switch to Add Controls tab
                        startIcon={<PlaylistAddIcon />}
                      >
                        Add Controls
                      </mui.Button>
                  </mui.Box>
                ) : (
                    <mui.List sx={{ pt: 0 }}> {/* Remove padding top from List */}
                    {mappedControls.map(control => (
                        <mui.Card 
                          key={control.id} 
                          sx={{ mb: 1.5 }} 
                          elevation={1} // Use elevation instead of outlined
                        >
                          <mui.CardContent sx={{ pb: '8px !important' }}> {/* Reduce bottom padding */}
                            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <mui.Chip 
                                label={control.CONTROL_ID} 
                              size="small" 
                                color="primary" 
                                variant="outlined" // Or filled for a different look
                                sx={{ fontWeight: 'medium' }}
                              />
                              <mui.Chip 
                                size="small" 
                                label={control.CONTROL_TYPE || 'Type N/A'} // Provide fallback
                              variant="outlined"
                               sx={{ height: 20, fontSize: '0.7rem' }} // Consistent chip style
                            />
                          </mui.Box>
                            <mui.Typography variant="body2" gutterBottom noWrap title={control.CONTROL_TITLE}>
                            {control.CONTROL_TITLE}
                          </mui.Typography>
                            <mui.Tooltip title={control.CONTROL_DESCRIPTION || ''}>
                          <mui.Typography 
                                variant="caption" 
                            color="textSecondary"
                            sx={{
                              display: '-webkit-box',
                              overflow: 'hidden',
                              WebkitBoxOrient: 'vertical',
                                  WebkitLineClamp: 2, // Show 2 lines max
                                  mb: 1 // Add margin below description
                            }}
                          >
                                {control.CONTROL_DESCRIPTION || 'No description available.'}
                          </mui.Typography>
                            </mui.Tooltip>
                            
                            {showRecommended && control.recommendationScore > 0 && (
                              <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 1 }}>
                                <mui.Rating
                                  value={Math.min(5, Math.ceil(control.recommendationScore / 4))} // Adjust score scaling
                                  readOnly
                                  size="small"
                                  emptyIcon={<StarOutlineIcon fontSize="inherit" />}
                                  sx={{ mr: 1 }}
                                />
                              </mui.Box>
                            )}
                        </mui.CardContent>
                          <mui.CardActions sx={{ justifyContent: 'flex-end', px: 1.5, pt: 0, pb: 1 }}>
                          <mui.Button
                              variant="text"
                            size="small"
                            color="error"
                              startIcon={processingControlId === control.id && successMessage.includes('unmapped') ? <mui.CircularProgress size={16} /> : <RemoveCircleOutlineIcon fontSize="small"/>}
                            onClick={() => handleUnmapControl(control)}
                            disabled={processingControlId === control.id}
                              sx={{ textTransform: 'none' }}
                          >
                            {processingControlId !== control.id && "Unmap"}
                          </mui.Button>
                        </mui.CardActions>
                      </mui.Card>
                    ))}
                  </mui.List>
                )}
                </TabPanel>
                
                {/* Tab Panel for Adding Controls */}
                <TabPanel value={activeTab} index={1}>
                   {/* Header for Add Controls */}
                  <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <mui.Typography variant="h6">
                        Control Library
                </mui.Typography>
                    <mui.Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <mui.Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setCreateControlModalOpen(true)}
                        sx={{ textTransform: 'none' }}
                  >
                        Create New Control
                  </mui.Button>
                  
                  <mui.Tooltip title={showRecommended ? "Show all controls" : "Show recommended controls"}>
                    <mui.Button
                      size="small"
                      color={showRecommended ? "primary" : "inherit"}
                      variant={showRecommended ? "contained" : "outlined"}
                      startIcon={showRecommended ? <StarIcon /> : <RecommendIcon />}
                      onClick={() => {
                        setShowRecommended(!showRecommended);
                            setSearchTerm(''); // Clear search when toggling recommendations
                            // Recommendations are generated on risk select, ensure list updates if needed
                            const controlsToFilter = Array.isArray(availableControls) ? availableControls : [];
                            if (!showRecommended) {
                                setFilteredControls(Array.isArray(recommendedControls) ? recommendedControls : []);
                            } else {
                                setFilteredControls(controlsToFilter); // Show all when switching off recommend
                            }
                          }}
                          disabled={controlsLoading || !recommendedControls || recommendedControls.length === 0} // Disable if loading or no recommendations
                        >
                          {showRecommended ? `Recommended (${recommendedControls.length})` : "Recommend"}
                    </mui.Button>
                  </mui.Tooltip>
                </mui.Box>
              </mui.Box>
              
                  {/* Search Bar for Controls */}
              <mui.TextField
                    placeholder={showRecommended ? "Search recommended..." : "Search control library..."}
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                      // Turn off recommendations if user starts searching
                  if (e.target.value && showRecommended) {
                    setShowRecommended(false);
                        // Refilter based on search term applied to all controls
                        const controlsToFilter = Array.isArray(availableControls) ? availableControls : [];
                        const filtered = controlsToFilter.filter(
                          control => 
                            control.CONTROL_ID?.toLowerCase().includes(e.target.value.toLowerCase()) ||
                            control.CONTROL_TITLE?.toLowerCase().includes(e.target.value.toLowerCase()) ||
                            (control.CONTROL_DESCRIPTION && typeof control.CONTROL_DESCRIPTION === 'string' && 
                            control.CONTROL_DESCRIPTION.toLowerCase().includes(e.target.value.toLowerCase()))
                        );
                        setFilteredControls(filtered);
                      } else if (!e.target.value && showRecommended) {
                          // If search cleared while recommendations active, show recommendations again
                          setFilteredControls(Array.isArray(recommendedControls) ? recommendedControls : []);
                      } else if (!e.target.value && !showRecommended) {
                           // If search cleared and not showing recommendations, show all
                           setFilteredControls(Array.isArray(availableControls) ? availableControls : []);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <mui.InputAdornment position="start">
                          <SearchIcon color="action" fontSize="small" />
                    </mui.InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <mui.InputAdornment position="end">
                          <mui.IconButton size="small" onClick={() => { 
                              setSearchTerm(''); 
                              // Reset list based on recommendation state
                              if (showRecommended) {
                                  setFilteredControls(Array.isArray(recommendedControls) ? recommendedControls : []);
                              } else {
                                  setFilteredControls(Array.isArray(availableControls) ? availableControls : []);
                              }
                          }}>
                        <ClearIcon fontSize="small" />
                      </mui.IconButton>
                    </mui.InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
                    disabled={controlsLoading} // Disable search while loading
              />
              
                  {/* Controls List (Available/Filtered/Recommended) */}
                  {controlsLoading ? (
                    <mui.Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 150 }}>
                  <mui.CircularProgress />
                </mui.Box>
              ) : filteredControls.length === 0 ? (
                <mui.Box 
                  sx={{ 
                    p: 3, 
                    textAlign: 'center',
                        backgroundColor: 'grey.100',
                    borderRadius: 2,
                        mt: 2
                  }}
                >
                  <mui.Typography variant="subtitle1" color="textSecondary" gutterBottom>
                        {showRecommended ? "No recommendations found" : "No controls match"}
                  </mui.Typography>
                      <mui.Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {searchTerm 
                          ? 'Try adjusting your search criteria.' 
                      : showRecommended
                            ? 'No suitable recommendations for this risk.'
                            : 'The control library is empty or no controls match.'}
                  </mui.Typography>
                  {showRecommended && (
                    <mui.Button
                      variant="outlined"
                      color="primary"
                      size="small"
                          sx={{ alignSelf: 'center' }}
                          onClick={() => {
                              setShowRecommended(false);
                              setFilteredControls(Array.isArray(availableControls) ? availableControls : []);
                          }}
                    >
                      Show All Controls
                        </mui.Button>
                      )}
                      {!showRecommended && !searchTerm && (
                         <mui.Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() => setCreateControlModalOpen(true)}
                            sx={{ textTransform: 'none' }}
                          >
                            Create a New Control
                    </mui.Button>
                  )}
                </mui.Box>
              ) : (
                    <mui.List sx={{ pt: 0 }}>
                  {filteredControls.map(control => {
                    const alreadyMapped = isControlMapped(control.id);
                        // Determine if *currently listed* item is recommended (only matters if showRecommended is true)
                        const isRecommendedBadge = showRecommended && recommendedControls.some(rc => rc.id === control.id);
                    
                    return (
                      <mui.Card 
                        key={control.id} 
                            elevation={1} // Use elevation
                        sx={{ 
                              mb: 1.5,
                              opacity: alreadyMapped ? 0.65 : 1, // Dim mapped controls slightly
                              backgroundColor: isRecommendedBadge 
                                ? 'rgba(232, 245, 233, 0.5)' // Softer green background for recommendation
                                : 'background.paper',
                              borderLeft: isRecommendedBadge ? `4px solid` : 'none',
                              borderLeftColor: 'success.light' // Lighter border for recommendation
                            }}
                          >
                            <mui.CardContent sx={{ pb: '8px !important' }}>
                              <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <mui.Chip 
                                    label={control.CONTROL_ID} 
                                  size="small" 
                                  color="primary"
                                    variant="outlined" // Or filled for a different look
                                    sx={{ fontWeight: 'medium' }}
                                />
                                  {isRecommendedBadge && (
                                <mui.Chip 
                                      icon={<StarIcon sx={{ fontSize: 14 }}/>}
                                  size="small" 
                                      label="Match" 
                                  color="success"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </mui.Box>
                            <mui.Chip 
                              size="small" 
                                  label={control.CONTROL_TYPE || 'Type N/A'} 
                              variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          </mui.Box>
                              <mui.Typography variant="body2" gutterBottom noWrap title={control.CONTROL_TITLE}>
                            {control.CONTROL_TITLE}
                          </mui.Typography>
                              <mui.Tooltip title={control.CONTROL_DESCRIPTION || ''}>
                          <mui.Typography 
                                  variant="caption" 
                            color="textSecondary"
                            sx={{
                              display: '-webkit-box',
                              overflow: 'hidden',
                              WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 2, // Show 2 lines max
                                    mb: 1 // Add margin below description
                            }}
                          >
                                  {control.CONTROL_DESCRIPTION || 'No description available.'}
                          </mui.Typography>
                              </mui.Tooltip>
                          
                          {showRecommended && control.recommendationScore > 0 && (
                                <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 1 }}>
                              <mui.Rating
                                    value={Math.min(5, Math.ceil(control.recommendationScore / 4))} // Adjust score scaling
                                readOnly
                                size="small"
                                emptyIcon={<StarOutlineIcon fontSize="inherit" />}
                                    sx={{ mr: 1 }}
                              />
                            </mui.Box>
                          )}
                        </mui.CardContent>
                            <mui.CardActions sx={{ justifyContent: 'flex-end', px: 1.5, pt: 0, pb: 1 }}>
                          {alreadyMapped ? (
                            <mui.Button
                                  variant="text"
                              size="small"
                                  color="success"
                                  startIcon={<CheckCircleIcon fontSize="small"/>}
                                  disabled // Already mapped
                                  sx={{ textTransform: 'none', fontWeight: 'medium' }}
                                >
                                  Mapped
                            </mui.Button>
                          ) : (
                            <mui.Button
                              variant="contained"
                              size="small"
                                  color={"primary"} // Consistent map button color
                                  startIcon={processingControlId === control.id && !successMessage.includes('unmapped') ? <mui.CircularProgress size={16} color="inherit" /> : <MapIcon fontSize="small"/>}
                              onClick={() => handleMapControl(control)}
                              disabled={processingControlId === control.id}
                                   sx={{ textTransform: 'none' }}
                            >
                              {processingControlId !== control.id && "Map Control"}
                            </mui.Button>
                          )}
                        </mui.CardActions>
                      </mui.Card>
                    );
                  })}
                </mui.List>
                  )}
                </TabPanel>
              </>
              )}
            </mui.Paper>
          </mui.Grid>
      </mui.Grid>
      
      {/* Bottom action bar */}
      <mui.Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          pt: 4,
          mt: 4,
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        <mui.Button
          variant="outlined"
          onClick={prevStep}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </mui.Button>
        
        <mui.Button
          variant="contained"
          color="primary"
          onClick={nextStep}
          endIcon={<ArrowForwardIcon />}
        >
          Continue to Testing
        </mui.Button>
      </mui.Box>
    </mui.Box>
  );
};

export default ControlMapper; 